import { use, useState, type ReactNode } from 'react';
import { ImageOff } from 'lucide-react';
import { describeApiError } from '@/lib/api-errors';
import { useLoader } from '@/hooks/use-loader';
import { Spinner } from '@/components/ui/spinner';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { WorktreeImageResult } from '@shared/agent-protocol';
import { MessagePictures, type MessagePicture } from './ChatMessageParts';
import { AgentPictureContext, isMachinePath, type PictureReader } from './agent-picture';

/**
 * SF-T-266: a picture the run's agent put in its message as Markdown (`![after](/abs/path/.screenshots/T-1/after.png)`),
 * shown in the message as T3 Code shows it. The file is on the run's machine, so it's read from the run's worktree
 * through the factory (`GET /api/runs/:id/image`); a click opens it full size.
 */

const FRESH_MS = 10 * 60_000;

const nameOf = (src: string, alt: string) => alt || src.split('/').pop() || 'Picture';
const dataUrl = (image: WorktreeImageResult) => `data:${image.mime};base64,${image.base64}`;

/** The picture, or `fallback` (a link to it) where there's no reader (the Chat page) or it's a web address. */
export function AgentPicture({ src, alt, fallback = null, className }: { src: string; alt: string; fallback?: ReactNode; className?: string }) {
  const where = use(AgentPictureContext);
  if (!where || !isMachinePath(src)) return <>{fallback}</>;
  return <LoadedPicture src={src} alt={alt} read={where.read} scope={where.scope} className={className} />;
}

/**
 * SF-T-266: several pictures in a row in the agent's message, as one collage (MessagePictures, as your own message's
 * pictures), each opening full size. `fallback`: them as they'd show apart, where there's no reader or one is a web address.
 */
export function AgentPictures({ pictures, fallback = null, className }: { pictures: { src: string; alt: string }[]; fallback?: ReactNode; className?: string }) {
  const where = use(AgentPictureContext);
  if (!where || !pictures.every((p) => isMachinePath(p.src))) return <>{fallback}</>;
  return <LoadedPictures pictures={pictures} read={where.read} scope={where.scope} className={className} />;
}

/** "Reading the picture from the machine…", or why it couldn't be read. */
function PictureNote({ name, title, error, className }: { name: string; title?: string; error: string | null; className?: string }) {
  return (
    <span className={cn('my-1 flex max-w-sm items-center gap-2 rounded-xl border bg-muted/40 px-3 py-2 text-2xs text-muted-foreground', className)}>
      {error ? <ImageOff className="size-4 shrink-0" aria-hidden /> : <Spinner size="xs" />}
      <span className="min-w-0">
        <span className="block truncate font-medium text-foreground" title={title}>
          {name}
        </span>
        {error ?? 'Reading the picture from the machine…'}
      </span>
    </span>
  );
}

/** One picture full size, over the page. */
function PictureDialog({ picture, onClose }: { picture: { name: string; url: string } | null; onClose: () => void }) {
  return (
    <Dialog open={!!picture} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[95dvh] w-auto max-w-[95vw] overflow-auto p-2 pt-10 sm:max-w-[95vw]">
        <DialogTitle className="sr-only">{picture?.name ?? 'Picture'}</DialogTitle>
        {picture ? <img src={picture.url} alt={picture.name} className="h-auto max-w-full" /> : null}
      </DialogContent>
    </Dialog>
  );
}

function LoadedPicture({ src, alt, read, scope, className }: { src: string; alt: string; read: PictureReader; scope: string; className?: string }) {
  const [open, setOpen] = useState(false);
  const loader = useLoader((signal) => read(src, { signal }), [src, read], { key: `agent-picture:${scope}:${src}`, staleMs: FRESH_MS });
  const name = nameOf(src, alt);
  const image = loader.data?.data?.image;
  if (!image) return <PictureNote name={name} title={src} error={loader.data?.error ? describeApiError(loader.data) : null} className={className} />;
  const url = dataUrl(image);
  return (
    <>
      <button type="button" className={cn('my-1 block max-w-full', className)} onClick={() => setOpen(true)} aria-label={`Open ${name} full size`} data-hint={src}>
        <img src={url} alt={name} className="max-h-80 max-w-full rounded-xl border object-contain" />
      </button>
      <PictureDialog picture={open ? { name, url } : null} onClose={() => setOpen(false)} />
    </>
  );
}

function LoadedPictures({ pictures, read, scope, className }: { pictures: { src: string; alt: string }[]; read: PictureReader; scope: string; className?: string }) {
  const [open, setOpen] = useState<{ name: string; url: string } | null>(null);
  const srcs = pictures.map((p) => p.src);
  const loader = useLoader((signal) => Promise.all(srcs.map((src) => read(src, { signal }))), [srcs.join('\n'), read], {
    key: `agent-pictures:${scope}:${srcs.join('\n')}`,
    staleMs: FRESH_MS,
  });
  const answers = loader.data;
  if (!answers) return <PictureNote name={`${pictures.length} pictures`} error={null} className={className} />;
  const shown: MessagePicture[] = [];
  const failed: { name: string; src: string; error: string }[] = [];
  pictures.forEach((p, i) => {
    const res = answers[i];
    const image = res?.data?.image;
    if (image) shown.push({ id: `${i}:${p.src}`, filename: nameOf(p.src, p.alt), url: dataUrl(image) });
    else failed.push({ name: nameOf(p.src, p.alt), src: p.src, error: res ? describeApiError(res) : 'Not read' });
  });
  return (
    <div className={cn('my-1 space-y-1', className)}>
      <MessagePictures pictures={shown} onOpen={(p) => setOpen({ name: p.filename, url: p.url })} />
      {failed.map((f) => (
        <PictureNote key={f.src} name={f.name} title={f.src} error={f.error} />
      ))}
      <PictureDialog picture={open} onClose={() => setOpen(null)} />
    </div>
  );
}
